import { Component, inject, Input, OnInit } from "@angular/core";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { excelDataAndDataTypeInterface } from "../bulk-upload/bulk-upload.component";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-excel-preview",
  templateUrl: "./excel-preview.component.html",
  styleUrls: ["./excel-preview.component.css"],
})
export class ExcelPreviewComponent implements OnInit {
  @Input() title: string = "";
  private toastr = inject(ToastrService);

  @Input() tableHeaders!: string[];
  @Input() canonicalHeaders!: string[];
  @Input() tableData!: any[];
  @Input() file!: File;
  @Input() requireEntriesList!: excelDataAndDataTypeInterface[];

  validationState: Record<number, Record<string, boolean>> = {};

  constructor(public activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    this.tableData.forEach((row, rowIndex) => {
    this.validationState[rowIndex] = {};
    this.canonicalHeaders.forEach((canonicalKey) => {         
      const dataType = this.getDataTypeForKey(canonicalKey);
      const value = row[canonicalKey];
      this.validationState[rowIndex][canonicalKey] = this.validateValue(dataType, value, canonicalKey);
    });
  });
  }

  cancel() {
    this.activeModal.dismiss("Excel Upload canceled by user");
  }

  confirm() {
    if (this.hasInvalidEntries()) {
      const invalidCols = this.getInvalidColumns();
      this.toastr.error(
        `Some required columns contain invalid or missing values.\nCheck and fix: ${invalidCols.map((c) => `"${c}"`).join(", ")}`
      );
      return;
    }
    this.activeModal.close({
      tableHeaders: this.tableHeaders.map((h) => this.resolveCanonicalKey(h)),
      tableData: this.tableData,
    });
  }

  toCamelCase(header: string): string {
    return header
      .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
      .replace(/^./, (str) => str.toLowerCase())
      .trim();
  }

  resolveCanonicalKey(displayHeader: string): string {
    const match = this.requireEntriesList?.find((e) => {
      if (e.header) {
        return e.header.toLowerCase() === displayHeader.toLowerCase();
      }
      return e.name.toLowerCase() === this.toCamelCase(displayHeader).toLowerCase();
    });
    return match ? match.name : this.toCamelCase(displayHeader);
  }

  getDataTypeForKey(canonicalKey: string): string {
    const match = this.requireEntriesList?.find(
      (e) => e.name.toLowerCase() === canonicalKey.toLowerCase()
    );
    return match?.dataType ?? "";
  }

  onCellValueChange(rowIndex: number, headerIndex: number, value: string) {
    const canonicalKey = this.canonicalHeaders[headerIndex];
    const dataType = this.getDataTypeForKey(canonicalKey);
    this.validationState[rowIndex][canonicalKey] = this.validateValue(dataType, value, canonicalKey);
  }

  validateValue(dataType: string, value: any, canonicalKey: string): boolean {
    const entry = this.requireEntriesList?.find(
      (e) => e.name.toLowerCase() === canonicalKey.toLowerCase()
    );

    const isRequired = entry?.required !== false;

    if (!isRequired && (value === null || value === undefined || value.toString().trim() === "")) {
      return true;
    }
    if (isRequired && (value === null || value === undefined || value.toString().trim() === "")) {
      return false;
    }
    if (!dataType) return true;

    const parts = dataType.split(" ");
    const type = parts[0].toLowerCase();
    switch (type) {
      case "string":    return value.length > 0;
      case "number":    return this.validateInt(value);
      case "time":      return this.validateTime(value);
      case "datetime":
      case "date":
      case "dateTime":  return this.validateDateTime(value);
      case "varchar":   return this.validateStringLimit(value, parseInt(parts[1] || "0"));
      case "decimal":   return this.validateDecimal(value, parseInt(parts[1] || "0"), parseInt(parts[2] || "0"));
      case "mobile":    return this.validateMobile(value);
      case "email":     return this.validateEmail(value);
      case "aadhar":    return this.validateAadhar(value);
      case "pan":       return this.validatePAN(value);
      default:          return true;
    }
  }

  hasInvalidEntries(): boolean {
    return Object.values(this.validationState).some((row) =>
      Object.values(row).some((cellValid) => !cellValid)
    );
  }

  getInvalidColumns(): string[] {
    const invalidCols = new Set<string>();
    Object.keys(this.validationState).forEach((rowIndex) => {
      const row = this.validationState[+rowIndex];
      Object.keys(row).forEach((key) => {
        if (!row[key]) {
          const entry = this.requireEntriesList?.find(
            (e) => e.name.toLowerCase() === key.toLowerCase()
          );
          invalidCols.add(entry?.header ?? key);
        }
      });
    });
    return Array.from(invalidCols);
  }

  isRequired(header: string): boolean {
    if (!this.requireEntriesList) return false;
    const canonicalKey = this.resolveCanonicalKey(header);
    const entry = this.requireEntriesList.find(
      (e) => e.name.toLowerCase() === canonicalKey.toLowerCase()
    );
    return entry?.required !== false;
  }

  validateStringLimit(value: string, limit: number): boolean {
    if (value === null || value === undefined) return false;
    return value.toString().trim().length <= limit && value.toString().length > 0;
  }

  validateInt(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^-?\d+$/.test(value.toString().trim());
  }

  validateDecimal(value: string, precision: number, scale: number): boolean {
    if (value === null || value === undefined) return false;
    const regex = new RegExp(`^-?\\d{1,${precision - scale}}(\\.\\d{1,${scale}})?$`);
    return regex.test(value.toString().trim());
  }

  validateTime(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/.test(value.toString().trim());
  }

  validateDateTime(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^\d{4}-\d{2}-\d{2}(?: ([01]\d|2[0-3]):([0-5]\d):([0-5]\d))?$/.test(value.toString().trim());
  }

  validateMobile(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^[6-9]\d{9}$/.test(value.toString().trim());
  }

  validateEmail(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.toString().trim());
  }

  validateAadhar(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^\d{12}$/.test(value.toString().trim());
  }

  validatePAN(value: string): boolean {
    if (value === null || value === undefined) return false;
    return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toString().trim());
  }
}